import {
  InventoryCtaStateResponse,
  WidgetCreateDTO,
  WidgetDuplicateDTO,
  WidgetHeartbeatItem,
  WidgetInterface,
  WidgetUpdateDTO
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

/**
 * Studio-side client for the v4 Widget Builder endpoints. Mirrors the
 * EmbedsAPI pattern; org-scoped methods take `orgId` first per workspace
 * convention.
 */
export class WidgetsAPI {
  static async list(orgId: number, includeArchived = false) {
    const qs = includeArchived ? `?includeArchived=true` : "";
    return APIService.Server.get<WidgetInterface[]>(`/widgets/org/${orgId}${qs}`);
  }

  static async create(orgId: number, dto: WidgetCreateDTO) {
    return APIService.Server.post<WidgetInterface>(`/widgets/org/${orgId}`, dto);
  }

  /** Patch operator-editable fields (B1). Status stays as-is; `publish` flips it. */
  static async update(orgId: number, widgetId: number, dto: WidgetUpdateDTO) {
    return APIService.Server.put<WidgetInterface>(`/widgets/org/${orgId}/widget/${widgetId}`, dto);
  }

  /** Flip status → published + stamp `lastPublishedAt`. Idempotent. */
  static async publish(orgId: number, widgetId: number) {
    return APIService.Server.post<WidgetInterface>(`/widgets/org/${orgId}/widget/${widgetId}/publish`);
  }

  /** Flip status → draft. Preserves `lastPublishedAt`. Idempotent. */
  static async unpublish(orgId: number, widgetId: number) {
    return APIService.Server.post<WidgetInterface>(`/widgets/org/${orgId}/widget/${widgetId}/unpublish`);
  }

  static async duplicate(orgId: number, widgetId: number, dto?: WidgetDuplicateDTO) {
    return APIService.Server.post<WidgetInterface>(`/widgets/org/${orgId}/widget/${widgetId}/duplicate`, dto);
  }

  static async archive(orgId: number, widgetId: number) {
    return APIService.Server.delete(`/widgets/org/${orgId}/widget/${widgetId}`);
  }

  static async restore(orgId: number, widgetId: number) {
    return APIService.Server.post<WidgetInterface>(`/widgets/org/${orgId}/widget/${widgetId}/restore`);
  }

  static async heartbeats(orgId: number) {
    return APIService.Server.get<WidgetHeartbeatItem[]>(`/widgets/org/${orgId}/heartbeats`);
  }

  /** Public — lazy per-visitor saved-unit lookup for the Inventory CTA. */
  static async inventoryCtaState(publicId: string, signedVisitorId: string) {
    return APIService.Server.get<InventoryCtaStateResponse>(
      `/widgets/${encodeURIComponent(publicId)}/inventory-cta-state?signedVisitorId=${encodeURIComponent(signedVisitorId)}`
    );
  }
}
